import {Component, inject} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {createImageCompositionFormValue} from '../../../../models/chatbot-options.model';
import {FrontendSettingsComponent} from './frontend-settings.component';
import {validateProductCardTextOverrides} from '../../../../models/product-card-texts.model';
import {ChatbotsService} from '../../../../services/objects/chatbots.service';
import {of} from 'rxjs';

@Component({
  selector: 'app-frontend-settings-visual-fixture',
  imports: [FrontendSettingsComponent],
  providers: [{
    provide: ChatbotsService,
    useValue: {
      proposeSampleMessagesAsync: () => of([]),
      uploadQuickStartImageAsync: (_chatbotId: string, file: File) => of({
        assetId: 'local-visual-fixture',
        contentType: file.type,
        size: file.size,
        url: `${window.location.origin}/assets/images/products/s6.jpg`
      })
    }
  }],
  template: `
    <main class="frontend-settings-visual-fixture">
      <app-frontend-settings
        chatbotId="fixture-chatbot"
        [frontendForm]="frontendForm"
        [settingsForm]="settingsForm"
        [messagesForm]="messagesForm"
        [localizationForm]="localizationForm"
        [localizationVariantsForm]="localizationVariantsForm"
        [validationTargetPath]="validationTargetPath"
        [aiProductCardsEnabled]="true"
        [expandedPanel]="quickStartsFixture ? 'localization' : null"
        [expandProductCardsPanel]="!quickStartsFixture">
      </app-frontend-settings>
    </main>
  `,
  styles: [`
    .frontend-settings-visual-fixture {
      display: block;
      margin: 0 auto;
      max-width: 920px;
      padding: 24px;
    }
  `]
})
export class FrontendSettingsVisualFixtureComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly fixtureState = this.route.snapshot.queryParamMap.get('frontendSettingsVisualFixture') || 'product-cards';
  private readonly ga4FixtureEnabled = this.fixtureState === 'product-cards-ga4';
  readonly quickStartsFixture = this.fixtureState === 'quick-starts';
  private readonly imageComposition = createImageCompositionFormValue({
    disclaimerText: 'Uploads are resized in the browser and sent only with the current request for visual matching.',
    eligibleCategories: ['Product', 'Category']
  }, false);

  readonly frontendForm = this.fb.group({
    generalSettings: this.fb.group({
      name: ['Fixture chatbot'],
      logo: [''],
      headerDisplayMode: [''],
      layoutPreset: [''],
      zIndex: [4500, [Validators.min(0), Validators.max(2147483647), Validators.pattern(/^\d+$/)]],
      logoWidth: [48],
      voiceEnabled: [false],
      attachmentsEnabled: [false],
      introMessage: ['Hello.'],
      firstMessage: ['How can I help you?'],
      inputPlaceholder: ['Type your message...'],
      submitButtonIcon: [''],
      resetButtonIcon: [''],
      attachButtonIcon: [''],
      voiceButtonIcon: [''],
      compactSubmitButtonIcon: [''],
      compactResetButtonIcon: [''],
      resetButtonTooltip: ['Click to reset your chat'],
      resetButtonConfirmationEnabled: [false],
      resetButtonConfirmationText: ['Click again to reset your chat'],
      sampleMessagesTitle: ['Quick start'],
      sampleMessagesPresentation: [''],
      sampleMessagesHoverBorderColor: [''],
      sampleMessagesClickAnimationEnabled: [false],
      sampleMessagesClickAnimationDelayMs: [140]
    }),
    imageComposition: this.fb.group({
      enabled: [false],
      cameraCaptureEnabled: [this.imageComposition.cameraCaptureEnabled],
      eligibleCategoriesText: [this.imageComposition.eligibleCategoriesText],
      disclaimerText: [this.imageComposition.disclaimerText],
      preferredRequestTransport: [this.imageComposition.preferredRequestTransport],
      supportedRequestTransports: [this.imageComposition.supportedRequestTransports],
      uploadExpiryHours: [this.imageComposition.uploadExpiryHours],
      resultExpiryHours: [this.imageComposition.resultExpiryHours],
      ownership: this.fb.group({
        uploadBoundTo: [this.imageComposition.ownership.uploadBoundTo],
        resultBoundTo: [this.imageComposition.ownership.resultBoundTo]
      })
    }),
    productCardSettings: this.fb.group({
      productCardTextOverrides: [{no: {details: 'Detaljer', testDrive: 'Prøvekjør'}, en: {details: 'More info'}}, validateProductCardTextOverrides],
      cardType: ['product', Validators.required],
      productCardPresentation: ['vertical-list'],
      productCardPresentationMode: ['fixed'],
      productCardDynamicPresentations: [[]],
      addToCartEnabled: [false],
      productCardEcommerceTrackingEnabled: [false],
      ga4EcommerceEventsEnabled: [this.ga4FixtureEnabled],
      cardLinkUrl: ['https://example.com/products'],
      cardUtmString: ['utm_source=visual_fixture'],
      cardLocale: ['en-US'],
      cardCurrency: ['USD'],
      productCardDetailsLabel: ['More info'],
      productCardTestDriveLabel: ['Test drive']
    }),
    dropdownSettings: this.fb.group({
      dropdownEnabled: [false],
      dropdownBtnText: ['Saved items'],
      dropdownBtnTextClose: ['Close'],
      dropdownMessage: ['Saved recommendations'],
      dropdownBtnIconEnabled: [false]
    }),
    chatAvatar: [''],
    chatAvatarEnabled: [true],
    styling: this.fb.group({
      advancedStylingEnabled: [true],
      chatBotWidth: ['1200'],
      font: ['Inter+Tight'],
      fontProvider: ['google'],
      fontFamily: [''],
      fontFallback: ['Inter, Arial, sans-serif'],
      fontPreset: [''],
      fontFaceRegularUrl: [''],
      fontFaceMediumUrl: [''],
      fontFaceSemiBoldUrl: [''],
      fontFaceBoldUrl: [''],
      widgetText: [''],
      textLinkColor: [''],
      chatBubbleAssistant: ['#FFFFFF'],
      chatBubbleAssistantBorderRadiusDesktop: ['0 12px 12px 12px'],
      chatBubbleAssistantBorderRadiusMobile: ['12px 12px 12px 0'],
      chatBubbleAssistantFontSizeDesktop: ['16px'],
      chatBubbleAssistantFontSizeMobile: ['14px'],
      chatBubbleUser: ['#000000'],
      chatBubbleUserBorderRadius: ['12px 12px 0 12px'],
      avatarAccent: ['#CCCCCC'],
      chatAvatarSizeDesktop: ['50px'],
      chatAvatarSizeMobile: ['16px'],
      ctaPrimaryBackground: ['#0000FF'],
      ctaPrimaryHover: ['#0000AA'],
      ctaPrimaryText: ['#FFFFFF'],
      ctaPrimaryTextHover: ['#EEEEEE'],
      badgeBackground: ['#FFFFFF'],
      badgeText: ['#000000'],
      inputBackground: ['#FFFFFF'],
      inputPlaceholder: ['#CCCCCC'],
      inputBorderColor: ['#D0D5DD'],
      inputHoverBorderColor: ['#121413'],
      inputActiveBorderColor: [''],
      inputActiveSendBackground: ['#121413'],
      inputActiveSendColor: ['#FFFFFF'],
      inputFocus: ['#AAAAAA'],
      inputShadowEnabled: [false],
      dpBtnBgColor: ['#FFFFFF'],
      dpBtnBgColorHover: ['#F0F0F0'],
      dpBtnColor: ['#000000'],
      dpBtnColorHover: ['#333333'],
      recIconColor: ['#444444'],
      recIconColorHover: ['#222222'],
      resetButton: ['#FF0000'],
      resetButtonHoverBackground: ['#E31100'],
      resetButtonHoverColor: ['#FFFFFF'],
      resetButtonPressedBackground: ['#121413'],
      resetButtonPressedColor: ['#FFFFFF'],
      closeButton: ['#FF0000'],
      sugBgColor: ['#FFFFFF'],
      sugIconBgColor: ['#EEEEEE'],
      sugIconColor: ['#111111'],
      cardBtnBgColor: ['#FFFFFF'],
      cardBtnBgColorHover: ['#F5F5F5'],
      cardBtnColor: ['#000000'],
      cardBtnColorHover: ['#333333'],
      cardBtnBorderColor: ['#FFFFFF'],
      cardBtnBorderColorHover: ['#F5F5F5'],
      cardAddToCartBgColor: ['#FFFFFF'],
      cardAddToCartBgColorHover: ['#F5F5F5'],
      cardAddToCartColor: ['#000000'],
      cardAddToCartColorHover: ['#333333'],
      cardAddToCartBorderColor: ['#FFFFFF'],
      cardAddToCartBorderColorHover: ['#F5F5F5'],
      cardWidth: [''],
      cardMaxWidth: ['273px'],
      cardBorderColor: ['transparent'],
      cardBackgroundColor: ['transparent'],
      cardPadding: ['1rem 0 0'],
      cardTitleFontSize: ['19px'],
      cardTitleFontWeight: ['600'],
      cardDescriptionFontSize: ['15px'],
      cardDescriptionVisible: [true],
      cardCtaVisible: [true],
      cardPriceVisible: [true],
      cardPriceFontSize: ['17px'],
      cardPriceFontWeight: ['700'],
      cardPriceCurrentColor: ['#b42318'],
      cardPriceSaleCurrentColor: ['#dd5500'],
      cardPriceOriginalColor: ['#111827'],
      cardPriceOriginalFontSize: ['15px'],
      cardImageAspectRatio: ['3:4'],
      cardCompactImageMaxHeight: [''],
      cardImageBackgroundColor: ['#f3f4f6'],
      cardBorderRadius: ['8px'],
      cardBadgeBackgroundColor: ['transparent'],
      cardBadgeTextColor: ['#121413'],
      cardSaleBadgeTextColor: ['#e31100'],
      cardAttributeBackgroundColor: ['#f4f4f4'],
      cardAttributeTextColor: ['#121413']
    }),
    css: [''],
    js: ['']
  });

  readonly settingsForm = this.fb.group({
    aiProductCardsEnabled: [true],
    aiProductCardsInstruction: ['Use product cards when recommendations include concrete products.']
  });

  readonly messagesForm = this.fb.group({
    sampleMessages: this.fb.array([
      this.fb.group({
        headline: ['Find jackets'],
        description: ['Show winter jackets'],
        imageUrl: [''],
        imageAlt: ['Jackets']
      })
    ]),
    errorMessages: this.fb.array([
      this.fb.group({message: ['Something went wrong.']})
    ]),
    waitMessages: this.fb.array([
      this.fb.group({message: ['One moment.']})
    ])
  });

  readonly localizationForm = this.fb.group({
    defaultLocale: ['en-US'],
    supportedLocales: [['en-US', 'nb-NO']],
    autoGenerate: [false]
  });

  readonly localizationVariantsForm: FormArray<FormGroup> = this.fb.array<FormGroup>([
    this.fb.group({
      locale: ['nb-NO'],
      generatedFromHash: ['fixture'],
      generatedAt: ['Manual fixture'],
      widgetText: ['Hei'],
      introMessage: ['Velkommen'],
      firstMessage: ['Hva kan vi hjelpe med?'],
      inputPlaceholder: ['Skriv meldingen din'],
      dropdownBtnText: ['Valg'],
      dropdownBtnTextClose: ['Lukk'],
      dropdownMessage: ['Velg et alternativ'],
      addToCartEnabled: [null],
      errorMessages: ['Noe gikk galt.'],
      waitMessages: ['Et øyeblikk.'],
      sampleMessages: this.fb.array([
        this.fb.group({
          headline: ['Finn jakker'],
          description: ['Se jakker for regnvær'],
          imageUrl: [''],
          imageAlt: ['Jakker']
        })
      ])
    })
  ]);

  readonly validationTargetPath = this.quickStartsFixture
    ? null
    : 'frontendForm.styling.cardPriceSaleCurrentColor';
}
